import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingCart, User, X, Phone, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { getCategories, getConfig } from "../lib/api";
import { catVisible } from "../lib/format";
import PromoBar from "./PromoBar";

const DEFAULT_MENU = [
  { label: "Tienda", link: "/tienda" },
  { label: "Nosotros", link: "/sobre-nosotros" },
  { label: "Contacto", link: "/contacto" },
];

export default function Header() {
  const { count } = useCart();
  const { customer } = useAuth();
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [promo, setPromo] = useState(null);
  const catsRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  useEffect(() => {
    const el = catsRef.current;
    if (!el) return;
    const check = () => setShowArrows(el.scrollWidth > el.clientWidth + 4);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [cats]);
  const scrollCats = (dir) => catsRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  const nav = useNavigate();

  useEffect(() => {
    getCategories().then((all) => setCats(all.filter((c) => !c.parent_id && catVisible(c, all)))).catch(() => {});
    getConfig().then(setPromo).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/tienda?buscar=${encodeURIComponent(q.trim())}`);
  };

  const globalPromo = promo && {
    enabled: promo.promo_enabled, text: promo.promo_text, link: promo.promo_link,
    bg: promo.promo_bg, countdown: promo.promo_countdown, start: promo.promo_start, end: promo.promo_end,
  };
  const menu = promo && promo.header_menu && promo.header_menu.length ? promo.header_menu : DEFAULT_MENU;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-border">
      <PromoBar promo={globalPromo} />
      <div className="bg-brand-ink text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between">
          <span className="font-mono tracking-tight">Directo de fábrica a su panadería</span>
          <a href="tel:+34931739860" className="hidden sm:flex items-center gap-1.5 hover:text-brand transition-colors" data-testid="header-phone">
            <Phone className="h-3 w-3" /> +34 931 739 860
          </a>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-4 h-20">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menú">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <Link to="/" className="flex items-center py-[5px]" data-testid="logo-link">
            <img src={"/logo.svg"} alt="FABRIPAN — directo de fábrica a su panadería" className="h-[70px] w-auto" />
          </Link>
          <form onSubmit={doSearch} className="hidden md:flex flex-1 max-w-md ml-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="header-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nombre o referencia..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-sm bg-secondary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </form>
          <nav className="ml-auto flex items-center gap-1 sm:gap-3">
            {menu.map((m, i) => (
              m.children && m.children.length ? (
                <div key={i} className="hidden lg:block relative group" data-testid={`nav-${i}`}>
                  <Link to={m.link || "#"} className="text-sm font-medium px-2 py-1 hover:text-brand transition-colors inline-flex items-center gap-1">{m.label}<ChevronDown className="h-3 w-3" /></Link>
                  <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-50">
                    <div className="bg-white border border-border rounded-sm shadow-lg py-1 min-w-44">
                      {m.children.map((s, si) => (
                        <Link key={si} to={s.link || "#"} className="block px-4 py-2 text-sm hover:bg-secondary hover:text-brand whitespace-nowrap" data-testid={`nav-${i}-sub-${si}`}>{s.label}</Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link key={i} to={m.link || "/"} className="hidden lg:block text-sm font-medium px-2 py-1 hover:text-brand transition-colors" data-testid={`nav-${i}`}>{m.label}</Link>
              )
            ))}
            <Link to={customer ? "/mi-cuenta" : "/acceso"} className="p-2 hover:text-brand transition-colors" data-testid="nav-account" aria-label="Mi cuenta">
              <User className="h-5 w-5" />
            </Link>
            <Link to="/carrito" className="relative p-2 hover:text-brand transition-colors" data-testid="nav-cart" aria-label="Carrito">
              <ShoppingCart className="h-5 w-5" />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-brand text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </Link>
          </nav>
        </div>
        <div className="hidden lg:block border-t border-border">
          <div className="relative flex items-center">
            {showArrows && (
              <button type="button" onClick={() => scrollCats(-1)} aria-label="Anterior" className="absolute left-0 z-10 h-11 px-1 bg-gradient-to-r from-white via-white to-transparent flex items-center" data-testid="cats-prev"><ChevronLeft className="h-5 w-5 text-black" /></button>
            )}
            <div ref={catsRef} className={`flex items-center gap-6 h-11 overflow-x-auto no-scrollbar scroll-smooth w-full ${showArrows ? "px-8" : ""}`}>
              {cats.map((c) => (
                <Link key={c.id} to={`/categoria/${c.slug}`} className="text-xs uppercase tracking-wide font-medium text-black hover:text-brand whitespace-nowrap transition-colors" data-testid={`nav-cat-${c.slug}`}>
                  {c.name}
                </Link>
              ))}
            </div>
            {showArrows && (
              <button type="button" onClick={() => scrollCats(1)} aria-label="Siguiente" className="absolute right-0 z-10 h-11 px-1 bg-gradient-to-l from-white via-white to-transparent flex items-center" data-testid="cats-next"><ChevronRight className="h-5 w-5 text-black" /></button>
            )}
          </div>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-border bg-white px-4 py-3 space-y-1 max-h-[70vh] overflow-y-auto">
          <form onSubmit={doSearch} className="mb-3">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="mobile-search-input" />
          </form>
          <Link to="/tienda" onClick={() => setOpen(false)} className="block py-2 font-medium">Tienda</Link>
          {cats.map((c) => (
            <Link key={c.id} to={`/categoria/${c.slug}`} onClick={() => setOpen(false)} className="block py-2 text-sm uppercase text-black">{c.name}</Link>
          ))}
          {menu.map((m, i) => (
            <div key={i}>
              <Link to={m.link || "/"} onClick={() => setOpen(false)} className="block py-2 font-medium">{m.label}</Link>
              {m.children && m.children.map((s, si) => (
                <Link key={si} to={s.link || "/"} onClick={() => setOpen(false)} className="block py-1.5 pl-4 text-sm text-muted-foreground">{s.label}</Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}
